import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ThousandSeparatorPipe } from '@inyova/inyova-shared';
import { IbanOption, WithdrawalForm, WithdrawalSteps, WithdrawType } from '@inyova/models';
import { toFixed } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-withdrawal-summary',
  templateUrl: './withdrawal-summary.component.html',
  styleUrls: ['./withdrawal-summary.component.scss'],
  standalone: false
})
export class WithdrawalSummaryComponent implements OnInit {
  @Input() accountInvested: boolean;
  @Input() currency: string;
  @Input() ibanList: IbanOption[];
  @Input() isMobileApp: boolean;
  @Input() minInvestment: number;
  @Input() totalAmount: number;
  @Input() withdrawForm: WithdrawalForm;
  @Input() withdrawalMinBuffer: number;
  @Input() isLoading: boolean;
  @Output() openStep: EventEmitter<WithdrawalSteps> = new EventEmitter<WithdrawalSteps>();
  @Output() prevStep: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitRequest: EventEmitter<void> = new EventEmitter<void>();
  title: string;
  summary: { step: WithdrawalSteps; title: string; value: string }[];
  showCallout = false;
  nextSteps: { step: string; icon: string; color: string }[];

  get isPartial() {
    return this.withdrawForm.withdrawal.type === WithdrawType.PARTIAL;
  }

  get minAmountText() {
    return this.thousandPipe.transform(this.minInvestment, this.currency);
  }

  constructor(
    private translateService: TranslateService,
    private thousandPipe: ThousandSeparatorPipe
  ) {}

  ngOnInit(): void {
    this.title = this.getTitle(this.withdrawForm.withdrawal.type);
    this.summary = this.getSummary(this.withdrawForm, this.currency);
    this.showCallout = this.displayCallout(this.withdrawForm, this.minInvestment, this.withdrawalMinBuffer);
    this.nextSteps = this.getNextSteps(this.withdrawForm.iban.type, this.withdrawForm.extra.keepAccount);
  }

  onOpenStep(step: WithdrawalSteps) {
    this.openStep.emit(step);
  }

  onContinue() {
    this.submitRequest.emit();
  }

  onBack() {
    this.prevStep.emit();
  }

  getTitle(type: WithdrawType): string {
    switch (type) {
      case WithdrawType.PARTIAL:
        return 'titlePartial';
      case WithdrawType.SAFE:
        return 'titleSafe';
      case WithdrawType.FULL:
        return 'titleFull';
      default:
        return '';
    }
  }

  getSummary(data: WithdrawalForm, currency: string) {
    const { withdrawal, iban, extra } = data;
    const summary = [
      {
        step: WithdrawalSteps.INITIAL,
        title: 'ACCOUNT.withdrawalV2.summary.amount.title',
        value: this.thousandPipe.transform(withdrawal.amount.toFixed(0), currency)
      },
      {
        step: WithdrawalSteps.IBAN,
        title: 'ACCOUNT.withdrawalV2.summary.bank.title',
        value: this.formatIban(iban.type, iban.iban, iban.newIban)
      }
    ];

    // Leave account open or close it for the full withdrawal
    if (withdrawal.type === WithdrawType.FULL) {
      summary.push({
        step: WithdrawalSteps.CLOSE,
        title: 'ACCOUNT.withdrawalV2.account.heading',
        value: `${this.translateService.instant(`ACCOUNT.withdrawalV2.account.radios.${extra.keepAccount ? 'open' : 'close'}.label`)}`
      });
    }
    return summary;
  }

  displayCallout(data: WithdrawalForm, minInvestment: number, buffer: number): boolean {
    const { type, amount } = data.withdrawal;
    if (type !== WithdrawType.PARTIAL) {
      return true;
    }
    const minAmount = this.totalAmount - minInvestment;
    const safeAmount = Number(toFixed(minAmount - this.totalAmount * buffer, 2));
    return amount < this.totalAmount && amount > safeAmount;
  }

  getNextSteps(ibanType: 'account' | 'new', keepAccount: boolean): { step: string; icon: string; color: string }[] {
    const next = [
      {
        step: 'sell',
        icon: 'copy-minus',
        color: 'color-core-brand-2-2'
      },
      {
        step: 'send',
        icon: 'circle-arrow-up',
        color: 'color-core-brand-6-2'
      },
      {
        step: 'arrive',
        icon: 'circle-arrow-down',
        color: 'color-core-brand-4-3'
      }
    ];

    if (ibanType === 'new') {
      next.unshift({
        step: 'verify',
        icon: 'file-check-2',
        color: 'color-core-brand-1-2'
      });
    }

    if (!keepAccount) {
      next.push({
        step: 'close',
        icon: 'user-x',
        color: 'color-core-neutral-3'
      });
    }

    return next;
  }

  formatIban(ibanType: 'account' | 'new', iban: string, newIban: string): string {
    if (ibanType === 'new') {
      return `${this.translateService.instant('ACCOUNT.withdrawalV2.summary.bank.new')} (${newIban})`;
    }
    const result = this.ibanList.find((ibanItem) => ibanItem.value === iban);
    return `${result?.title} (${result?.value})`;
  }
}
